import { Button } from '@common/components/Button';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar';
import { getBookingRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { useTranslations } from '@lang';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { Price, Rate } from 'shared-types/Common';
import { RoomExtra } from 'shared-types/Room';
import { MultiRoomSearch } from 'shared-types';
import { RoomRateFormStepNames } from './FormSteps';
import { ExtrasWrapper } from './ExtrasStep/ExtrasWrapper.styles';
import { combineRoomExtras, mapExtrasToBasket, priceWithExtras } from './ExtrasStep/utils';
import { RoomRateFormValues } from '../RoomRateForm.schema';
import { Extras } from './Extras/Extras';
import { toPayableStayPrice } from './selectedEntity.helper';

type ExtraFormEntry = { isSelected?: boolean; quantity?: number; option?: number };
const normalizeExtrasForBasket = (extras: ExtraFormEntry[] | undefined) =>
  extras?.map((entry) => {
    if (entry && typeof entry.option === 'number') {
      return { ...entry, option: { value: entry.option } };
    }
    return entry;
  });

type ExtrasStepProps = {
  stickyBarId: string;
  goToNextStep: () => void;
  addToBasket: (...args: any[]) => Promise<void>;
  selectedEntity?: Rate;
  metaEntity: { type: 'room' | 'rate'; id: string; title?: string };
  entity: { type: 'room' | 'rate'; id: string };
  availableExtras: RoomExtra[] | null;
  isMultiRoom: boolean;
  rooms?: MultiRoomSearch;
};

export const ExtrasStep = ({
  stickyBarId,
  addToBasket,
  goToNextStep,
  selectedEntity,
  metaEntity,
  entity,
  availableExtras,
  isMultiRoom,
  rooms = [],
}: ExtrasStepProps) => {
  const { t } = useTranslations();
  const router = useRouter();
  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useFormContext<RoomRateFormValues>();

  const guestValues = useWatch({ name: 'guests' }) ?? [];
  const extrasValues = useWatch({ name: 'extras' });
  const areGuestsLoaded = (guestValues as RoomRateFormValues['guests']).some(
    (g) => g.firstName || g.lastName || g.title
  );

  useEffect(() => {
    if (areGuestsLoaded) return;
    router.push({
      pathname: getBookingRoute(router.pathname, metaEntity.type),
      query: {
        id: metaEntity.id,
        bookingStep: RoomRateFormStepNames.GUESTS,
        [entity.type]: entity.id,
      },
    });
  }, [areGuestsLoaded]);

  const onSubmit = async (values: RoomRateFormValues) => {
    const { guests, extras, note } = values;
    if (isMultiRoom) {
      const extrasRecord = (extras ?? {}) as Record<string | number, ExtraFormEntry[]>;
      const entExtras = rooms.map((_, i) => {
        const roomExtras: (ExtraFormEntry | undefined)[] = extrasRecord[`room-${i}`] ?? [];
        const merged = (availableExtras ?? []).map(
          (_, j) => roomExtras[j] ?? (extrasRecord[j] as ExtraFormEntry | undefined)
        );
        return mapExtrasToBasket(
          availableExtras,
          normalizeExtrasForBasket(merged as ExtraFormEntry[])
        );
      });
      await addToBasket({ guestDetails: guests, extras: entExtras, note });
    } else {
      const entExtras = mapExtrasToBasket(availableExtras, normalizeExtrasForBasket(extras as any));
      await addToBasket({ guestDetails: guests, extras: entExtras, note });
    }
    goToNextStep();
  };

  let displayPrice: Price | null = null;
  if (selectedEntity) {
    if (isMultiRoom) {
      const roomKeyedExtras = Object.fromEntries(
        Object.entries((extrasValues as Record<string, any>) ?? {}).filter(([k]) =>
          k.startsWith('room-')
        )
      ) as Record<string, any[]>;
      const multiRoomExtras = combineRoomExtras(roomKeyedExtras);
      displayPrice = priceWithExtras(
        { pricePerStay: toPayableStayPrice(selectedEntity) as Price },
        availableExtras,
        multiRoomExtras
      );
    } else {
      displayPrice = priceWithExtras(
        { pricePerStay: toPayableStayPrice(selectedEntity) as Price },
        availableExtras,
        (extrasValues as ExtraFormEntry[]) ?? []
      );
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <ExtrasWrapper>
        <Extras extras={availableExtras} isMultiRoom={isMultiRoom} rooms={rooms} />
      </ExtrasWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          displayedPrice={displayPrice}
          button={
            <Button
              isPrimary
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              {t('booking.continue')}
            </Button>
          }
        />
      </Portal>
    </form>
  );
};
